import React, { useEffect, useState } from 'react';
import { useCRM } from '../../context/CRMContext';
import { useDarkMode } from '../../App';
import { subscribeMyChatThreads } from '../../firebase/firestore';
import ChatKeyGate from '../Chat/ChatKeyGate';
import ChatPanel from '../Chat/ChatPanel';

const PAGE_TITLES = {
  dashboard:    { title: 'Dashboard',     sub: 'Welcome back, good to see you 👋' },
  leads:        { title: 'Leads',         sub: 'Manage and track all your leads' },
  pipeline:     { title: 'Pipeline',      sub: 'Drag & drop to move leads through stages' },
  followups:    { title: 'Follow-ups',    sub: 'Stay on top of every lead interaction' },
  campaigns:    { title: 'Campaigns',     sub: 'WhatsApp, Email & SMS campaigns' },
  integrations: { title: 'Integrations', sub: 'Connect Facebook, Instagram, WhatsApp & more' },
  webforms:     { title: 'Web Forms',     sub: 'Embeddable lead capture forms for your website' },
  settings:     { title: 'Settings',      sub: 'Configure your CRM to fit your workflow' },
};

export default function Header() {
  const { currentPage, setCurrentPage, followups, leads, currentUser, setAddLeadModalOpen, leaveRequests } = useCRM();
  const { dark, toggle: toggleDark } = useDarkMode();
  const [showNotifs, setShowNotifs] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatThreads, setChatThreads] = useState([]);
  const [searchVal, setSearchVal]   = useState('');
  const page = PAGE_TITLES[currentPage] || PAGE_TITLES.dashboard;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  // Thread metadata (memberIds/lastMessageAt/lastReadAt) is unencrypted —
  // only message content is — so the unread badge can subscribe directly,
  // with no need for the chat identity/key flow to be loaded first. See
  // CHAT_E2E_DESIGN.md §6 and the "Secure Chat — threads & messages" section
  // of firebase/firestore.js.
  useEffect(() => {
    if (!currentUser?.companyId || !currentUser?.id) { setChatThreads([]); return; }
    const unsub = subscribeMyChatThreads(currentUser.companyId, currentUser.id, setChatThreads);
    return unsub;
  }, [currentUser?.companyId, currentUser?.id]);

  const chatUnreadCount = chatThreads.filter(t => {
    if (!t.lastMessageAt || t.lastMessageSenderId === currentUser?.id) return false;
    const lastRead = t.lastReadAt?.[currentUser?.id];
    if (!lastRead) return true;
    return (t.lastMessageAt.seconds || 0) > (lastRead.seconds || 0);
  }).length;

  const overdueFollowups = followups.filter(f => f.status === 'overdue');
  const newLeadsToday = leads.filter(l => new Date(l.createdAt) > new Date(Date.now() - 86400000));

  // Leave notifications — admin-only. A request needs the admin's attention
  // either because it's brand new (status === 'pending') or because someone
  // posted in its discussion thread since the admin last opened it
  // (adminUnread, set by addLeaveMessage/handleSubmit in Leaves.js). One
  // request can trip both; dedupe by id so it only counts once.
  const leaveAlerts = isAdmin
    ? (leaveRequests || []).filter(r => r.status === 'pending' || r.adminUnread)
    : [];
  const notifCount = overdueFollowups.length + newLeadsToday.length + leaveAlerts.length;

  return (
    <header style={{
      height: 64,
      borderBottom: '1px solid rgba(0,0,0,0.05)',
      display: 'flex',
      alignItems: 'center',
      padding: '0 24px',
      gap: 16,
      background: 'var(--bg-primary)',
      backdropFilter: 'blur(12px)',
      position: 'sticky',
      top: 0,
      zIndex: 9,
      flexShrink: 0,
    }}>
      {/* Page title */}
      <div style={{ flex: 1 }}>
        <h1 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>{page.title}</h1>
        <p style={{ fontSize: 12, color: '#475569', marginTop: 1 }}>{page.sub}</p>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', width: 240 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2"
          style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}>
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          className="crm-input"
          placeholder="Search leads, contacts..."
          value={searchVal}
          onChange={e => setSearchVal(e.target.value)}
          style={{ paddingLeft: 34, fontSize: 13, height: 36, borderRadius: 9 }}
        />
      </div>

      {/* Quick add */}
      <button className="btn-primary" onClick={() => { setCurrentPage('leads'); setAddLeadModalOpen(true); }} style={{ padding: '8px 14px', fontSize: 13, gap: 6 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Add Lead
      </button>

      {/* Dark / Light mode toggle */}
      <button
        onClick={toggleDark}
        title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        style={{
          background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
          border: '1px solid ' + (dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
          borderRadius: 9, width: 36, height: 36, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
          transition: 'all 0.2s',
        }}
      >
        {dark ? '☀️' : '🌙'}
      </button>

      {/* Team Chat — pill button in the header toolbar. Clearly labeled so
          users know what it is at a glance. Unread count badge floats as a
          superscript at top-right of the button (like a mobile app badge).
          Active state uses a gradient tint + subtle glow. The
          ChatKeyGate + ChatPanel drawer mounts here (not fixed-positioned). */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <button
          onClick={() => setShowChat(v => !v)}
          title="Team Chat"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '0 13px', height: 36, borderRadius: 9,
            border: showChat
              ? '1px solid rgba(124,58,237,0.45)'
              : chatUnreadCount > 0 ? '1px solid rgba(239,68,68,0.35)' : '1px solid rgba(0,0,0,0.09)',
            background: showChat
              ? 'linear-gradient(135deg, rgba(124,58,237,0.13) 0%, rgba(59,130,246,0.10) 100%)'
              : chatUnreadCount > 0 ? 'rgba(239,68,68,0.06)' : 'rgba(0,0,0,0.04)',
            color: showChat ? '#7c3aed' : chatUnreadCount > 0 ? '#dc2626' : '#475569',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            boxShadow: showChat ? '0 0 0 3px rgba(124,58,237,0.10)' : 'none',
            transition: 'all 0.18s',
          }}
          onMouseEnter={e => { if (!showChat) { e.currentTarget.style.border = '1px solid rgba(124,58,237,0.3)'; e.currentTarget.style.color = '#7c3aed'; } }}
          onMouseLeave={e => { if (!showChat) { e.currentTarget.style.border = chatUnreadCount > 0 ? '1px solid rgba(239,68,68,0.35)' : '1px solid rgba(0,0,0,0.09)'; e.currentTarget.style.color = chatUnreadCount > 0 ? '#dc2626' : '#475569'; } }}
        >
          {/* Chat bubble icon */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span>Team Chat</span>
        </button>
        {/* Superscript unread badge — floats above the button, only shown when count > 0 */}
        {chatUnreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -7, right: -7,
            background: '#ef4444', color: '#fff', borderRadius: 99,
            minWidth: 18, height: 18, padding: '0 5px',
            fontSize: 10, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #fff',
            boxShadow: '0 1px 4px rgba(239,68,68,0.5)',
            lineHeight: 1, pointerEvents: 'none',
          }}>{chatUnreadCount > 99 ? '99+' : chatUnreadCount}</span>
        )}
      </div>

      {showChat && (
        <ChatKeyGate onClose={() => setShowChat(false)}>
          <ChatPanel onClose={() => setShowChat(false)} />
        </ChatKeyGate>
      )}

      {/* Notifications */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => { setShowNotifs(v => !v); setShowChat(false); }}
          style={{
            background: showNotifs ? 'rgba(124,58,237,0.15)' : 'rgba(0,0,0,0.05)',
            border: '1px solid ' + (showNotifs ? 'rgba(124,58,237,0.3)' : 'rgba(255,255,255,0.08)'),
            borderRadius: 9, width: 36, height: 36, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#475569', position: 'relative',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          {notifCount > 0 && (
            <span style={{
              position: 'absolute', top: -3, right: -3,
              background: '#ef4444', color: '#fff', borderRadius: '50%',
              width: 16, height: 16, fontSize: 9, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid #f8fafc',
            }}>{notifCount}</span>
          )}
        </button>

        {showNotifs && (
          <div style={{
            position: 'absolute', top: 44, right: 0, width: 320,
            background: '#ffffff', border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: 14, overflow: 'hidden', zIndex: 100,
            boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
            animation: 'slideUp 0.15s ease',
          }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Notifications</span>
              <span style={{ fontSize: 11, color: '#7c3aed', cursor: 'pointer' }}>Mark all read</span>
            </div>

            {overdueFollowups.slice(0, 3).map(f => (
              <div key={f.id} style={{ padding: '11px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: 10, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 500 }}>Overdue: {f.title}</div>
                  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 1 }}>Past due</div>
                </div>
              </div>
            ))}

            {newLeadsToday.slice(0, 2).map(l => (
              <div key={l.id} style={{ padding: '11px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: 10, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 500 }}>New lead: {l.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>via {l.source} · just now</div>
                </div>
              </div>
            ))}

            {leaveAlerts.slice(0, 3).map(r => (
              <div key={r.id}
                onClick={() => { setCurrentPage('leaves'); setShowNotifs(false); }}
                style={{ padding: '11px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: 10, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 500 }}>
                    {r.status === 'pending' ? `Leave request from ${r.userName || r.userEmail}` : `New message on ${r.userName || r.userEmail}'s leave`}
                  </div>
                  <div style={{ fontSize: 11, color: '#92400e', marginTop: 1 }}>
                    {r.status === 'pending' ? 'Awaiting approval' : 'Unread reply'}
                  </div>
                </div>
              </div>
            ))}

            {notifCount === 0 && (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: '#475569', fontSize: 13 }}>
                You're all caught up! 🎉
              </div>
            )}
          </div>
        )}
      </div>

      {/* Avatar + profile dropdown */}
      <div style={{ position: 'relative' }}>
        <div
          onClick={() => setShowProfile(v => !v)}
          style={{ cursor: 'pointer' }}
          title={currentUser.name}
        >
          <div className="avatar" style={{
            background: currentUser.avatar?.bg || '#7c3aed',
            color: currentUser.avatar?.color || '#fff',
            fontSize: 13, fontWeight: 700,
            border: '2px solid rgba(124,58,237,0.4)',
            width: 35, height: 35,
          }}>
            {(currentUser.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
          </div>
        </div>

        {showProfile && (
          <div style={{
            position: 'absolute', top: 44, right: 0, width: 220,
            background: '#ffffff', border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: 14, overflow: 'hidden', zIndex: 100,
            boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
          }} onClick={() => setShowProfile(false)}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{currentUser.name}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{currentUser.email}</div>
              <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600, marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{currentUser.role}</div>
            </div>
            <div
              onClick={() => setCurrentPage('settings')}
              style={{ padding: '10px 16px', fontSize: 13, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
              My Profile
            </div>
            <div
              onClick={() => setCurrentPage('settings')}
              style={{ padding: '10px 16px', fontSize: 13, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
              Settings
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
